import { useEffect, useState, useMemo } from "react";
import {
  Star,
  Clock,
  Globe,
  Play,
  ChevronUp,
  ChevronDown,
  FileText,
  BookOpen,
  Layers3,
  Video,
  Volume2,
  Image as ImageIcon,
  Code,
  ClipboardCheck,
  Award,
  Captions,
  Search,
  User,
  Calendar,
  BarChart3,
  MessageSquare,
  Settings,
  Edit,
  AlertCircle,
  Download,
  Plus,
  Trash2,
  GripVertical,
  Save,
  Brain,
  Target,
  RefreshCw,
} from "lucide-react";
import { ApiService } from "../../services/api";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import checkPermission from "../../utils/permission";
import { useModal } from "../../hooks/useModal";
import TabPage, { TabItemProps } from "../../components/dynamic/Tab";
import AcademicCalendar from "./components/calendar/AcademicCalendar";
import SectionModal from "./components/sections/SectionModal";
import ContentModal from "./components/content/ContentModal";
import TestQuestionsModal from "./components/content/TestQuestionsModal";
import Modal from "../../components/ui/Modal";
import ConfirmModal from "../../components/modals/ConfirmModal";
function EditFormModal({
  editModalState,
  setEditModalState,
  handleSaveEdit,
}: {
  editModalState: any;
  setEditModalState: any;
  handleSaveEdit: (title: string, description?: string) => Promise<void>;
}) {
  const [title, setTitle] = useState(editModalState.title);
  const [description, setDescription] = useState(editModalState.description);
  const [loading, setLoading] = useState(false);

  const onSave = async () => {
    if (!title.trim()) {
      return;
    }
    if (!description.trim()) {
      alert("Açıklama zorunludur");
      return;
    }
    setLoading(true);
    try {
      await handleSaveEdit(title, description);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={editModalState.isOpen}
      onClose={() => setEditModalState((prev: any) => ({ ...prev, isOpen: false }))}
      title={editModalState.type === "section" ? "Bölümü Düzenle" : "İçeriği Düzenle"}
      size="md">
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Başlık</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            placeholder="Başlık giriniz"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Açıklama <span className="text-red-500">*</span>
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
            placeholder="Açıklama giriniz (zorunlu)"
          />
        </div>
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
          <button
            onClick={() => setEditModalState((prev: any) => ({ ...prev, isOpen: false }))}
            disabled={loading}
            className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors disabled:opacity-50">
            İptal
          </button>
          <button
            onClick={onSave}
            disabled={loading || !title.trim() || !description.trim()}
            className="px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
            {loading ? "Kaydediliyor..." : "Kaydet"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

export default function CourseDetailPage() {
  const { lessonId } = useParams();
  const location = useLocation();
  const { showAlert, showConfirm } = useModal();
  const navigate = useNavigate();

  const [lesson, setLesson] = useState<any>(null);
  const [expandedSections, setExpandedSections] = useState({});
  const [activeContentTab, setActiveContentTab] = useState("content");
  const [sections, setSections] = useState<any[]>([]);
  const [lessonProgress, setLessonProgress] = useState<any>(null);
  const [sectionLoading, setSectionLoading] = useState<Record<string, boolean>>({});

  const [isSectionModalOpen, setIsSectionModalOpen] = useState(false);
  const [isContentModalOpen, setIsContentModalOpen] = useState(false);
  const [selectedSectionId, setSelectedSectionId] = useState<string>("");
  const [isVideoGainsModalOpen, setIsVideoGainsModalOpen] = useState(false);
  const [selectedVideoContent, setSelectedVideoContent] = useState<{
    id: string;
    title: string;
    sectionId: string;
  } | null>(null);
  const [isTestQuestionsModalOpen, setIsTestQuestionsModalOpen] = useState(false);
  const [selectedTestContent, setSelectedTestContent] = useState<{
    id: string;
    title: string;
    sectionId: string;
  } | null>(null);
  const [videoGains, setVideoGains] = useState<any[]>([]);
  const [isLoadingGains, setIsLoadingGains] = useState(false);
  const [newGain, setNewGain] = useState({
    gain: "",
    startTime: "",
    endTime: "",
  });
  const [editingGain, setEditingGain] = useState<any>(null);
  const [deleteConfirmModal, setDeleteConfirmModal] = useState({
    isOpen: false,
    gainId: "",
    gainTitle: "",
  });
  const [isDeleting, setIsDeleting] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  // Transkript görüntüleme
  const [transcriptModal, setTranscriptModal] = useState<{
    isOpen: boolean;
    loading: boolean;
    data: any;
    title: string;
    searchTerm: string;
  }>({ isOpen: false, loading: false, data: null, title: "", searchTerm: "" });

  // Section learning plan önizleme
  const [learningPlanModal, setLearningPlanModal] = useState<{
    isOpen: boolean;
    loading: boolean;
    data: any;
    title: string;
    sectionId: string;
  }>({ isOpen: false, loading: false, data: null, title: "", sectionId: "" });

  const [draggedSection, setDraggedSection] = useState<string | null>(null);
  const [draggedContent, setDraggedContent] = useState<{
    id: string;
    sectionId: string;
  } | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const hasAddPermission = checkPermission("lesson_add");
  const hasEditPermission = checkPermission("lesson_update");

  const hasDeletePermission = checkPermission("lesson_delete");
  const hasSectionAddPermission = checkPermission("lesson_section_add");
  const hasSectionUpdatePermission = checkPermission("lesson_section_update");
  const hasSectionDeletePermission = checkPermission("lesson_section_delete");
  const hasReorderPermission = hasSectionUpdatePermission || hasSectionAddPermission;
  const hasCalendarPermission = checkPermission("lesson_calendar_list");
  
  const isParentOnly =
    checkPermission("parent_report_list") &&
    !checkPermission("homework_report_list") &&
    !checkPermission("test_report_list") &&
    !checkPermission("homework_list");

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const currentTab = params.get("tab");

    if (currentTab === "lessons") {
      navigate("/lessons", { replace: true });
    } else if (currentTab === "homeworks") {
      navigate("/lessons?tab=homeworks", { replace: true });
    } else if (currentTab === "deficiencies") {
      navigate("/lessons?tab=deficiencies", { replace: true });
    } else if (currentTab === "reports") {
      navigate("/lessons?tab=reports", { replace: true });
    } else if (currentTab === "error-booklet") {
      navigate("/lessons?tab=error-booklet", { replace: true });
    }
  }, [location.search, navigate]);

  useEffect(() => {
    const fetchLesson = async () => {
      const res = await ApiService.getLessonPreview(lessonId!);
      setLesson(res);
    };
    fetchLesson();
  }, []);

  useEffect(() => {
    fetchSections();
  }, [lessonId]);

  useEffect(() => {
    if (lessonId) {
      const savedProgress = localStorage.getItem(`lessonProgress_${lessonId}`);
      if (savedProgress) {
        try {
          setLessonProgress(JSON.parse(savedProgress));
        } catch (error) {
          setLessonProgress(null);
        }
      }
    }
  }, [lessonId]);

  const fetchSections = async () => {
    if (!lessonId) return;
    try {
      const res = await ApiService.getLessonSections(lessonId);
      const sectionsWithoutContents = res.map((section: any) => ({
        ...section,
        contents: undefined,
      }));
      setSections(sectionsWithoutContents);
      sectionsWithoutContents.forEach((section: any, idx: number) => {
        if ((expandedSections as any)[idx]) {
          if (typeof section.contents === "undefined" && !sectionLoading[section.id]) {
            loadSectionContents(section.id, idx);
          }
        }
      });
    } catch (err) {
      setSections([]);
    }
  };

  const loadSectionContents = async (sectionId: string, index: number) => {
    if (!lessonId) return;
    setSectionLoading((prev) => ({ ...prev, [sectionId]: true }));
    try {
      const contents = await ApiService.getSectionContents(lessonId, sectionId);
      setSections((prev) => {
        const arr = [...prev];
        if (arr[index] && arr[index].id === sectionId) {
          arr[index] = { ...arr[index], contents } as any;
        } else {
          const found = arr.findIndex((s: any) => s.id === sectionId);
          if (found !== -1) arr[found] = { ...arr[found], contents } as any;
        }
        return arr;
      });
    } catch (_) {
      setSections((prev) => {
        const arr = [...prev];
        const found = arr.findIndex((s: any) => s.id === sectionId);
        if (found !== -1) arr[found] = { ...arr[found], contents: [] } as any;
        return arr;
      });
    } finally {
      setSectionLoading((prev) => ({ ...prev, [sectionId]: false }));
    }
  };

  const handleAddSection = () => {
    setIsSectionModalOpen(true);
  };

  const handleCloseSectionModal = () => {
    setIsSectionModalOpen(false);
    fetchSections();
  };

  const handleDelete = async (type: "section" | "content", id: string, title: string, sectionId?: string) => {
    const confirmed = await showConfirm({
      title: type === "section" ? "Bölümü Sil" : "İçeriği Sil",
      message: `"${title}" ${type === "section" ? "bölümünü" : "içeriğini"} silmek istediğinize emin misiniz?`,
      confirmText: "Sil",
      confirmButtonVariant: "danger",
    });

    if (!confirmed) return;

    try {
      if (type === "section") {
        await ApiService.deleteLessonSection(lessonId!, id);
      } else {
        await ApiService.deleteSectionContent(lessonId!, sectionId!, id);
      }
      fetchSections();
    } catch (err) {
      await showAlert({
        title: "Hata",
        message: "Silinirken bir hata oluştu.",
        type: "error",
      });
    }
  };

  const handleAddContent = (sectionId: string) => {
    setSelectedSectionId(sectionId);
    setIsContentModalOpen(true);
  };

  const handleContentModalClose = () => {
    setIsContentModalOpen(false);
    setSelectedSectionId("");
    fetchSections();
  };

  const handleOpenVideoGainsModal = async (content: any, sectionId: string) => {
    setSelectedVideoContent({
      id: content.id,
      title: content.title,
      sectionId: sectionId,
    });
    setIsVideoGainsModalOpen(true);
    await fetchVideoGains(content.id, sectionId);
  };

  const handleCloseVideoGainsModal = () => {
    setIsVideoGainsModalOpen(false);
    setSelectedVideoContent(null);
    setVideoGains([]);
    setNewGain({ gain: "", startTime: "", endTime: "" });
    setEditingGain(null);
    setDeleteConfirmModal({ isOpen: false, gainId: "", gainTitle: "" });
    setIsDeleting(false);
    setIsUpdating(false);
  };

  const handleOpenTestQuestionsModal = (content: any, sectionId: string) => {
    setSelectedTestContent({
      id: content.id,
      title: content.title,
      sectionId: sectionId,
    });
    setIsTestQuestionsModalOpen(true);
  };

  const handleCloseTestQuestionsModal = () => {
    setIsTestQuestionsModalOpen(false);
    setSelectedTestContent(null);
  };

  const handleOpenTranscriptModal = async (content: any) => {
    setTranscriptModal({
      isOpen: true,
      loading: true,
      data: null,
      title: content.title || "Transkript",
      searchTerm: "",
    });
    try {
      const data = await ApiService.getContentTranscript(content.id);
      setTranscriptModal((prev) => ({ ...prev, loading: false, data }));
    } catch (err: any) {
      setTranscriptModal((prev) => ({
        ...prev,
        loading: false,
        data: { error: err?.message || "Transkript yüklenemedi" },
      }));
    }
  };

  const handleCloseTranscriptModal = () => {
    setTranscriptModal({ isOpen: false, loading: false, data: null, title: "", searchTerm: "" });
  };

  const handleOpenLearningPlanModal = async (section: any, refresh = false) => {
    setLearningPlanModal({
      isOpen: true,
      loading: true,
      data: null,
      title: section.title || "Öğrenme Planı",
      sectionId: section.id,
    });
    try {
      const data = await ApiService.getSectionLearningPlan(section.id, { refresh });
      setLearningPlanModal((prev) => ({ ...prev, loading: false, data }));
    } catch (err: any) {
      setLearningPlanModal((prev) => ({
        ...prev,
        loading: false,
        data: { error: err?.message || "Plan yüklenemedi" },
      }));
    }
  };

  const handleCloseLearningPlanModal = () => {
    setLearningPlanModal({ isOpen: false, loading: false, data: null, title: "", sectionId: "" });
  };

  const handleStartLessonFromPlan = async () => {
    const sectionId = learningPlanModal.sectionId;
    if (!sectionId) return;
    try {
      const result = await ApiService.prepareSessionPlanFromSection(sectionId);
      if (!result.sessionPlanId) {
        // no_data veya not_found — kullanıcıya bildir
        setLearningPlanModal((prev) => ({
          ...prev,
          data: { ...prev.data, error: result.message || "Plan oluşturulamadı" },
        }));
        return;
      }

      const baseContext = {
        lessonId: lessonId,
        lessonTitle: lesson?.title || "",
        topicId: sectionId,
        topicTitle: learningPlanModal.data?.section?.title || learningPlanModal.title,
        source: "sectionLearningPlan",
        sessionPlanId: result.sessionPlanId,
      };

      // AI tutor panelini aç
      window.dispatchEvent(
        new CustomEvent("toggleAITutorPanel", { detail: { context: baseContext } })
      );
      handleCloseLearningPlanModal();
    } catch (err: any) {
      setLearningPlanModal((prev) => ({
        ...prev,
        data: { ...prev.data, error: err?.message || "Plan başlatılamadı" },
      }));
    }
  };

  const fetchVideoGains = async (contentId: string, sectionId: string) => {
    if (!lessonId) return;

    setIsLoadingGains(true);
    try {
      const response = await ApiService.getLessonSectionContentGains(lessonId, sectionId, contentId);
      setVideoGains(response?.data || response || []);
    } catch (error) {
      console.error("Kazanımlar yüklenirken hata:", error);
      setVideoGains([]);
    } finally {
      setIsLoadingGains(false);
    }
  };

  const handleCreateGain = async () => {
    if (!lessonId || !selectedVideoContent || !newGain.gain || !newGain.startTime || !newGain.endTime) return;

    try {
      const payload = {
        gain: newGain.gain,
        startTime: toHHMMSS(newGain.startTime),
        endTime: toHHMMSS(newGain.endTime),
      };
      await ApiService.createLessonSectionContentGain(lessonId, selectedVideoContent.sectionId, selectedVideoContent.id, payload);

      setNewGain({ gain: "", startTime: "", endTime: "" });
      await fetchVideoGains(selectedVideoContent.id, selectedVideoContent.sectionId);
    } catch (error) {
      console.error("Kazanım eklenirken hata:", error);
    }
  };

  const handleEditGain = (gain: any) => {
    setEditingGain(gain);
    setNewGain({
      gain: gain.gain,
      startTime: formatDisplayTime(gain.startTime),
      endTime: formatDisplayTime(gain.endTime),
    });
  };

  const handleUpdateGain = async () => {
    if (!lessonId || !selectedVideoContent || !editingGain || !newGain.gain || !newGain.startTime || !newGain.endTime) return;

    setIsUpdating(true);
    try {
      const payload = {
        gain: newGain.gain,
        startTime: toHHMMSS(newGain.startTime),
        endTime: toHHMMSS(newGain.endTime),
      };
      await ApiService.updateLessonSectionContentGain(
        lessonId,
        selectedVideoContent.sectionId,
        selectedVideoContent.id,
        editingGain.id,
        payload
      );
      setEditingGain(null);
      setNewGain({ gain: "", startTime: "", endTime: "" });
      await fetchVideoGains(selectedVideoContent.id, selectedVideoContent.sectionId);
    } catch (error) {
      console.error("Kazanım güncellenirken hata:", error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleOpenDeleteConfirm = (gainId: string, gainTitle: string) => {
    setDeleteConfirmModal({
      isOpen: true,
      gainId,
      gainTitle,
    });
  };

  const handleCloseDeleteConfirm = () => {
    setDeleteConfirmModal({ isOpen: false, gainId: "", gainTitle: "" });
  };

  const handleSectionDragStart = (e: React.DragEvent, sectionId: string) => {
    if (!hasReorderPermission) {
      e.preventDefault();
      return;
    }
    setDraggedSection(sectionId);
    setIsDragging(true);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", "");
  };

  const handleSectionDragOver = (e: React.DragEvent) => {
    if (!hasReorderPermission || !draggedSection) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleSectionDrop = async (e: React.DragEvent, targetSectionId: string) => {
    e.preventDefault();
    if (!hasReorderPermission || !draggedSection || draggedSection === targetSectionId) {
      setDraggedSection(null);
      setIsDragging(false);
      return;
    }

    try {
      const fromIndex = sections.findIndex((s: any) => s.id === draggedSection);
      const toIndex = sections.findIndex((s: any) => s.id === targetSectionId);

      if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) {
        return;
      }

      const reorderedSections = [...sections];
      const [movedSection] = reorderedSections.splice(fromIndex, 1);
      reorderedSections.splice(toIndex, 0, movedSection);

      const updatePromises = reorderedSections
        .map((section: any, index: number) => {
          const newOrder = index + 1;
          if (section.order !== newOrder) {
            return ApiService.updateLessonSection(lessonId!, section.id, {
              order: newOrder,
            });
          }
          return null;
        })
        .filter(Boolean);

      if (updatePromises.length > 0) {
        await Promise.all(updatePromises);
        await fetchSections();
      }
    } catch (error) {
      await showAlert({
        title: "Hata",
        message: "Bölüm sıralaması güncellenirken bir hata oluştu.",
        type: "error",
      });
    } finally {
      setDraggedSection(null);
      setIsDragging(false);
    }
  };

  const handleSectionDragEnd = () => {
    setDraggedSection(null);
    setIsDragging(false);
  };

  const handleContentDragStart = (e: React.DragEvent, contentId: string, sectionId: string) => {
    if (!hasReorderPermission) {
      e.preventDefault();
      return;
    }
    setDraggedContent({ id: contentId, sectionId });
    setIsDragging(true);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", "");
  };

  const handleContentDragOver = (e: React.DragEvent) => {
    if (!hasReorderPermission || !draggedContent) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleContentDrop = async (e: React.DragEvent, targetContentId: string, targetSectionId: string) => {
    e.preventDefault();
    if (!hasReorderPermission || !draggedContent || draggedContent.id === targetContentId) {
      setDraggedContent(null);
      setIsDragging(false);
      return;
    }

    if (draggedContent.sectionId !== targetSectionId) {
      await showAlert({
        title: "Uyarı",
        message: "İçerikler sadece aynı bölüm içinde sıralanabilir.",
        type: "warning",
      });
      setDraggedContent(null);
      setIsDragging(false);
      return;
    }

    try {
      const section = sections.find((s: any) => s.id === targetSectionId);
      if (!section || !section.contents) {
        return;
      }

      const contents = [...section.contents];
      const fromIndex = contents.findIndex((c: any) => c.id === draggedContent.id);
      const toIndex = contents.findIndex((c: any) => c.id === targetContentId);

      if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) {
        return;
      }

      const reorderedContents = [...contents];
      const [movedContent] = reorderedContents.splice(fromIndex, 1);
      reorderedContents.splice(toIndex, 0, movedContent);

      const updatePromises = reorderedContents
        .map((content: any, index: number) => {
          const newOrder = index + 1;
          if (content.order !== newOrder) {
            const normalizedType = content.type === "PDF" ? "FILE" : content.type === "TEST" ? "EXAM" : content.type || "TEXT";
            return ApiService.updateSectionContent(lessonId!, targetSectionId, content.id, {
              type: normalizedType,
              order: newOrder,
            });
          }
          return null;
        })
        .filter(Boolean);

      if (updatePromises.length > 0) {
        await Promise.all(updatePromises);
        await fetchSections();
      }
    } catch (error) {
      await showAlert({
        title: "Hata",
        message: "İçerik sıralaması güncellenirken bir hata oluştu.",
        type: "error",
      });
    } finally {
      setDraggedContent(null);
      setIsDragging(false);
    }
  };

  const handleContentDragEnd = () => {
    setDraggedContent(null);
    setIsDragging(false);
  };

  const handleConfirmDelete = async () => {
    if (!lessonId || !selectedVideoContent || !deleteConfirmModal.gainId) return;

    setIsDeleting(true);
    try {
      await ApiService.deleteLessonSectionContentGain(
        lessonId,
        selectedVideoContent.sectionId,
        selectedVideoContent.id,
        deleteConfirmModal.gainId
      );
      await fetchVideoGains(selectedVideoContent.id, selectedVideoContent.sectionId);
      handleCloseDeleteConfirm();
    } catch (error) {
      console.error("Kazanım silinirken hata:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const formatTimeInput = (value: string): string => {
    let digits = value.replace(/\D/g, "").slice(0, 4);

    if (digits.length === 0) return "";

    if (digits.length <= 2) {
      const m = Math.min(parseInt(digits || "0", 10), 59);
      return digits.length === 2 && m !== parseInt(digits, 10) ? "59" : digits;
    }

    let mm = digits.slice(0, 2);
    let ss = digits.slice(2);

    const mVal = Math.min(parseInt(mm || "0", 10), 59);
    mm = String(mVal).padStart(2, "0");

    const sVal = Math.min(parseInt(ss || "0", 10), 59);
    ss = digits.length === 3 ? String(sVal) : String(sVal).padStart(2, "0");

    return `${mm}:${ss}`;
  };

  const handleTimeInputChange = (field: "startTime" | "endTime", value: string) => {
    const formattedValue = formatTimeInput(value);
    setNewGain({ ...newGain, [field]: formattedValue });
  };

  const formatDisplayTime = (timeString: string): string => {
    if (!timeString) return "";

    if (timeString.includes(":")) {
      const parts = timeString.split(":");
      if (parts.length === 3) {
        return `${parts[1]}:${parts[2]}`;
      } else if (parts.length === 2) {
        return timeString;
      }
    }

    return timeString;
  };

  const toHHMMSS = (mmss: string): string => {
    if (!mmss) return "";
    if (mmss.includes(":")) {
      const [mRaw = "0", sRaw = "0"] = mmss.split(":");
      const mm = String(parseInt(mRaw || "0", 10)).padStart(2, "0");
      const ss = String(parseInt(sRaw || "0", 10)).padStart(2, "0");
      return `00:${mm}:${ss}`;
    }
    return mmss;
  };

  const handleDeleteLesson = async () => {
    const confirmed = await showConfirm({
      title: "Dersi Sil",
      message: `"${lesson?.title}" dersini silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.`,
      confirmText: "Sil",
      confirmButtonVariant: "danger",
    });

    if (!confirmed) return;

    try {
      await ApiService.deleteLesson(lessonId!);
      await showAlert({
        title: "Başarılı",
        message: "Ders başarıyla silindi.",
        type: "success",
      });
      navigate("/lessons");
    } catch (error: any) {
      await showAlert({
        title: "Hata",
        message: error.message || "Ders silinirken bir hata oluştu. Lütfen tekrar deneyin.",
        type: "error",
      });
    }
  };

  const [editModalState, setEditModalState] = useState<{
    isOpen: boolean;
    type: "section" | "content";
    id: string;
    title: string;
    description: string;
    sectionId?: string;
    contentType?: string;
  }>({
    isOpen: false,
    type: "section",
    id: "",
    title: "",
    description: "",
  });

  const handleOpenEditModal = (
    type: "section" | "content",
    id: string,
    title: string,
    description: string = "",
    sectionId?: string,
    contentType?: string
  ) => {
    setEditModalState({
      isOpen: true,
      type,
      id,
      title,
      description,
      sectionId,
      contentType,
    });
  };

  const handleSaveEdit = async (newTitle: string, newDescription?: string) => {
    try {
      if (editModalState.type === "section") {
        await ApiService.updateLessonSection(lessonId!, editModalState.id, {
          title: newTitle,
          description: newDescription || "",
        });
      } else {
        await ApiService.updateSectionContent(lessonId!, editModalState.sectionId!, editModalState.id, {
          title: newTitle,
          content: newDescription || "",
          type: editModalState.contentType || "TEXT",
        });
      }
      fetchSections();
      setEditModalState((prev) => ({ ...prev, isOpen: false }));
    } catch (err) {
      await showAlert({
        title: "Hata",
        message: "Güncellerken bir hata oluştu.",
        type: "error",
      });
    }
  };

  const toggleSection = (index: number, sectionId: string) => {
    const wasOpen = !!(expandedSections as any)[index];
    if (!wasOpen) {
      const section = sections[index];
      const notLoaded = typeof (section as any)?.contents === "undefined";
      if (section && notLoaded && !sectionLoading[sectionId]) {
        loadSectionContents(sectionId, index);
      }
    }
    setExpandedSections((prev) => ({
      ...prev,
      [index]: !prev[index as keyof typeof prev],
    }));
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("tr-TR", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const getGradientByLessonId = (lessonId: string) => {
    const numericId = lessonId.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0);
    const gradientIndex = numericId % 20;

    const gradients = [
      "from-blue-600 via-blue-500 to-indigo-500", // Mavi - Güvenilir
      "from-emerald-600 via-teal-500 to-cyan-500", // Zümrüt-Turkuaz - Doğal
      "from-purple-600 via-violet-500 to-indigo-500", // Mor - Yaratıcı
      "from-teal-600 via-emerald-500 to-green-500", // Turkuaz-Yeşil - Pozitif
      "from-indigo-600 via-blue-500 to-sky-500", // İndigo-Gökyüzü - Açık
      "from-cyan-600 via-teal-500 to-emerald-500", // Cyan - Teknolojik
      "from-violet-600 via-purple-500 to-pink-500", // Violet - Sofistike
      "from-sky-600 via-blue-500 to-indigo-500", // Gökyüzü - Ferah
      "from-green-600 via-emerald-500 to-teal-500", // Yeşil - Büyüme
      "from-slate-600 via-blue-500 to-indigo-500", // Slate-Mavi - Profesyonel
      "from-rose-500 via-pink-500 to-purple-500", // Gül - Sıcak
      "from-orange-500 via-amber-500 to-yellow-500", // Turuncu-Sarı - Enerjik
      "from-emerald-500 via-green-500 to-teal-500", // Doğa Tonları - Huzurlu
      "from-blue-500 via-indigo-500 to-purple-500", // Mavi-Mor - Dengeli
      "from-teal-500 via-cyan-500 to-sky-500", // Turkuaz-Gökyüzü - Fresh
      "from-purple-500 via-indigo-500 to-blue-500", // Mor-Mavi - Modern
      "from-amber-500 via-orange-500 to-red-500", // Amber-Turuncu - Sıcak
      "from-green-500 via-teal-500 to-cyan-500", // Yeşil Tonları - Doğal
      "from-indigo-500 via-purple-500 to-violet-500", // İndigo-Mor - Lüks
      "from-slate-500 via-gray-500 to-zinc-500", // Nötr Tonlar - Minimal
    ];

    return gradients[gradientIndex];
  };

  const getProgressContentIcon = (type: string) => {
    switch (type?.toUpperCase()) {
      case "VIDEO":
        return <Video className="w-4 h-4 text-purple-600" />;
      case "AUDIO":
        return <Volume2 className="w-4 h-4 text-blue-600" />;
      case "IMAGE":
        return <ImageIcon className="w-4 h-4 text-green-600" />;
      case "TEXT":
        return <FileText className="w-4 h-4 text-gray-600" />;
      case "FILE":
      case "PDF":
        return <FileText className="w-4 h-4 text-red-600" />;
      case "CODE":
        return <Code className="w-4 h-4 text-yellow-600" />;
      case "TEST":
      case "EXAM":
        return <ClipboardCheck className="w-4 h-4 text-emerald-600" />;
      default:
        return <FileText className="w-4 h-4 text-gray-600" />;
    }
  };

  const getContentTypeIcon = (type: string) => {
    switch (type?.toUpperCase()) {
      case "VIDEO":
        return <Play className="w-6 h-6 text-purple-600" />;
      case "AUDIO":
        return <Volume2 className="w-6 h-6 text-blue-600" />;
      case "IMAGE":
        return <ImageIcon className="w-6 h-6 text-green-600" />;
      case "TEXT":
        return <FileText className="w-6 h-6 text-gray-600" />;
      case "FILE":
      case "PDF":
        return <FileText className="w-6 h-6 text-red-600" />;
      case "CODE":
        return <Code className="w-6 h-6 text-indigo-600" />;
      case "TEST":
      case "EXAM":
        return <ClipboardCheck className="w-6 h-6 text-green-600" />;
      default:
        return <BookOpen className="w-6 h-6 text-blue-600" />;
    }
  };

  const getContentTypeLabel = (type: string) => {
    switch (type?.toUpperCase()) {
      case "VIDEO":
        return "Video";
      case "AUDIO":
        return "Ses";
      case "IMAGE":
        return "Resim";
      case "TEXT":
        return "Metin";
      case "FILE":
        return "Dosya";
      case "PDF":
        return "PDF";
      case "CODE":
        return "Kod";
      case "TEST":
      case "EXAM":
        return "Test";
      default:
        return "İçerik";
    }
  };

  const getContentTypeBgClass = (type: string) => {
    switch (type?.toUpperCase()) {
      case "VIDEO":
        return "bg-purple-100";
      case "AUDIO":
        return "bg-blue-100";
      case "IMAGE":
        return "bg-green-100";
      case "TEXT":
        return "bg-gray-100";
      case "FILE":
      case "PDF":
        return "bg-red-100";
      case "CODE":
        return "bg-indigo-100";
      case "TEST":
      case "EXAM":
        return "bg-green-100";
      default:
        return "bg-blue-100";
    }
  };

  const getContentTypeBadgeClass = (type: string) => {
    switch (type?.toUpperCase()) {
      case "VIDEO":
        return "bg-purple-100 text-purple-700";
      case "AUDIO":
        return "bg-blue-100 text-blue-700";
      case "IMAGE":
        return "bg-green-100 text-green-700";
      case "TEXT":
        return "bg-gray-100 text-gray-700";
      case "FILE":
      case "PDF":
        return "bg-red-100 text-red-700";
      case "CODE":
        return "bg-indigo-100 text-indigo-700";
      case "TEST":
      case "EXAM":
        return "bg-green-100 text-green-700";
      default:
        return "bg-blue-100 text-blue-700";
    }
  };

  const renderCourseContentTabs = () => {
    const contentTabs = [
      { id: "content", label: "Ders İçeriği", icon: BookOpen },
      ...(hasCalendarPermission ? [{ id: "calendar", label: "Akademik Takvim", icon: Calendar }] : []),
      { id: "stats", label: "İstatistikler", icon: BarChart3 },
    ];

    return (
      <div className="bg-white border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex space-x-4 sm:space-x-8 overflow-x-auto scrollbar-hide -mx-4 sm:mx-0 px-4 sm:px-0 whitespace-nowrap">
            {contentTabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveContentTab(tab.id)}
                  className={`py-3 sm:py-4 px-2 border-b-2 font-medium text-sm transition-colors shrink-0 ${
                    activeContentTab === tab.id
                      ? "border-purple-500 text-purple-600"
                      : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}>
                  <div className="flex items-center gap-2">
                    <Icon className="w-4 h-4" />
                    {tab.label}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const renderActiveContentTab = () => {
    switch (activeContentTab) {
      case "content":
        return (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-8 overflow-hidden">
              <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-6 border-b border-gray-200">
                <div className="flex items-center justify-between flex-col sm:flex-row gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center">
                      <BookOpen className="w-7 h-7 text-purple-600" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">Ders İçeriği</h2>
                      <p className="text-gray-600 mt-1">Ders bölümlerini görüntüleyin ve yönetin.</p>
                    </div>
                  </div>
                  {hasSectionAddPermission && (
                    <button
                      onClick={handleAddSection}
                      className="bg-green-600 text-white px-5 py-2.5 rounded-lg font-medium hover:bg-green-700 transition-colors flex items-center gap-2 shadow-sm hover:shadow-md">
                      <Plus className="w-4 h-4" />
                      Bölüm Ekle
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="space-y-4">
              {sections?.length > 0 ? (
                sections
                  .sort((a: any, b: any) => (a.order || 0) - (b.order || 0))
                  .map((section: any, index: number) => (
                    <div
                      key={section.id || index}
                      className={`bg-white border border-gray-200 rounded-lg shadow-sm transition-all duration-200 hover:shadow-md ${
                        draggedSection === section.id ? "opacity-50 scale-95" : ""
                      } ${hasReorderPermission ? "cursor-grab active:cursor-grabbing" : ""}`}
                      draggable={hasReorderPermission}
                      onDragStart={(e) => handleSectionDragStart(e, section.id)}
                      onDragOver={handleSectionDragOver}
                      onDrop={(e) => handleSectionDrop(e, section.id)}
                      onDragEnd={handleSectionDragEnd}>
                      <div className="border-b border-gray-200 bg-gray-50 p-4">
                        <div className="flex items-center justify-between">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleSection(index, section.id);
                            }}
                            className="flex items-center flex-1 text-left">
                            {expandedSections[index as keyof typeof expandedSections] ? (
                              <ChevronUp className="w-5 h-5 mr-3 text-purple-600" />
                            ) : (
                              <ChevronDown className="w-5 h-5 mr-3 text-gray-400" />
                            )}
                            <div className="flex-1">
                              <h3 className="font-semibold text-gray-900 text-lg">{section.title}</h3>
                              {section.description && <p className="text-sm text-gray-600 mt-1">{section.description}</p>}
                            </div>
                          </button>
                          <div className="flex items-center gap-3 ml-4">
                            <div className="flex items-center gap-1">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  void handleOpenLearningPlanModal(section);
                                }}
                                className="p-2 text-blue-600 hover:text-white hover:bg-blue-600 rounded-lg transition-colors"
                                title="Öğrenme Planımı Görüntüle (bu bölümdeki yanlışlarımdan)">
                                <Brain className="w-4 h-4" />
                              </button>
                              {hasSectionUpdatePermission && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenEditModal("section", section.id, section.title, section.description || "");
                                  }}
                                  className="p-2 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                                  title="Bölümü Düzenle">
                                  <Edit className="w-4 h-4" />
                                </button>
                              )}
                              {hasSectionDeletePermission && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/lessons/${lessonId}/sections/${section.id}/delete`);
                                  }}
                                  className="p-2 text-red-600 hover:text-white hover:bg-red-600 rounded-lg transition-colors"
                                  title="Bölümü Sil">
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )}
                              {hasReorderPermission && (
                                <button
                                  onClick={(e) => e.stopPropagation()}
                                  className="p-2 text-gray-400 hover:text-gray-600 cursor-grab rounded-lg transition-colors"
                                  title="Bölümü Sırala (Sürükle-Bırak)">
                                  <GripVertical className="w-5 h-5 md:w-4 md:h-4" />
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      {expandedSections[index as keyof typeof expandedSections] && (
                        <div className="p-4">
                          {sectionLoading[section.id] ? (
                            <div className="text-center py-8">
                              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto mb-4"></div>
                              <p className="text-gray-600">İçerikler yükleniyor...</p>
                            </div>
                          ) : section?.contents?.length > 0 ? (
                            <div className="space-y-3">
                              {section.contents
                                .sort((a: any, b: any) => (a.order || 0) - (b.order || 0))
                                .map((content: any, contentIndex: number) => (
                                  <div
                                    key={`${content.id}-${content.order || contentIndex}`}
                                    className={`group flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-4 border border-gray-200 rounded-lg transition-all cursor-pointer hover:border-purple-300 hover:bg-purple-50 ${
                                      draggedContent?.id === content.id ? "opacity-50 scale-95" : ""
                                    } ${hasReorderPermission ? "cursor-grab active:cursor-grabbing" : ""}`}
                                    draggable={hasReorderPermission}
                                    onDragStart={(e) => handleContentDragStart(e, content.id, section.id)}
                                    onDragOver={handleContentDragOver}
                                    onDrop={(e) => handleContentDrop(e, content.id, section.id)}
                                    onDragEnd={handleContentDragEnd}
                                    onClick={() => navigate(`/lessons/play/${lessonId}?contentId=${content.id}`)}>
                                    <div className="flex items-center w-full">
                                      <div
                                        className={`w-12 h-12 rounded-xl flex items-center justify-center mr-4 ${getContentTypeBgClass(
                                          content.type
                                        )}`}>
                                        {getContentTypeIcon(content.type)}
                                      </div>
                                      <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-1">
                                          <h4 className="font-medium text-gray-900 group-hover:text-purple-700">{content.title}</h4>
                                          <span
                                            className={`text-xs font-medium px-2 py-1 rounded-full ${getContentTypeBadgeClass(
                                              content.type
                                            )}`}>
                                            {getContentTypeLabel(content.type)}
                                          </span>
                                        </div>
                                        {content.description && <p className="text-sm text-gray-500 mt-1">{content.description}</p>}
                                        <div className="flex items-center gap-4 mt-2">
                                          <span className="text-xs text-gray-400 flex items-center">
                                            <Clock className="w-3 h-3 mr-1" />
                                            {content.duration || `${content?.metadata?.time || 0} dk`}
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-2 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity mt-2 sm:mt-0">
                                      {content.type?.toUpperCase() === "VIDEO" && (() => {
                                        const segCount = Number(
                                          content?.transcriptSegmentCount ??
                                          content?.metadata?.transcription?.segmentCount ??
                                          0
                                        );
                                        const hasTranscript = segCount > 0;
                                        return (
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleOpenTranscriptModal(content);
                                            }}
                                            className={`p-2 rounded-lg transition-colors ${
                                              hasTranscript
                                                ? "text-blue-600 hover:text-white hover:bg-blue-600"
                                                : "text-gray-300 hover:text-gray-500 hover:bg-gray-100"
                                            }`}
                                            title={
                                              hasTranscript
                                                ? `Transkripti Görüntüle (${segCount} segment)`
                                                : "Transkript yok"
                                            }>
                                            <Captions className="w-4 h-4" />
                                          </button>
                                        );
                                      })()}
                                      {content.type?.toUpperCase() === "VIDEO" && hasEditPermission && (
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleOpenVideoGainsModal(content, section.id);
                                          }}
                                          className="p-2 text-green-600 hover:text-white hover:bg-green-600 rounded-lg transition-colors"
                                          title="Kazanım Ekle">
                                          <Award className="w-4 h-4" />
                                        </button>
                                      )}
                                      {(content.type?.toUpperCase() === "EXAM" || content.type?.toUpperCase() === "TEST") &&
                                        hasSectionUpdatePermission && (
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleOpenTestQuestionsModal(content, section.id);
                                            }}
                                            className="p-2 text-purple-600 hover:text-white hover:bg-purple-600 rounded-lg transition-colors"
                                            title="Soru Düzenle">
                                            <ClipboardCheck className="w-4 h-4" />
                                          </button>
                                        )}
                                      {hasEditPermission && (
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleOpenEditModal(
                                              "content",
                                              content.id,
                                              content.title,
                                              content.content || "",
                                              section.id,
                                              content.type
                                            );
                                          }}
                                          className="p-2 text-gray-400 hover:text-purple-600 hover:bg-purple-100 rounded-lg transition-colors"
                                          title="İçeriği Düzenle">
                                          <Edit className="w-4 h-4" />
                                        </button>
                                      )}
                                      {hasDeletePermission && (
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleDelete("content", content.id, content.title, section.id);
                                          }}
                                          className="p-2 text-red-600 hover:text-white hover:bg-red-600 rounded-lg transition-colors"
                                          title="İçeriği Sil">
                                          <Trash2 className="w-4 h-4" />
                                        </button>
                                      )}
                                      {hasReorderPermission && (
                                        <button
                                          onClick={(e) => e.stopPropagation()}
                                          className="p-2 text-gray-400 hover:text-gray-600 cursor-grab rounded-lg transition-colors"
                                          title="İçeriği Sırala (Sürükle-Bırak)">
                                          <GripVertical className="w-5 h-5 md:w-4 md:h-4" />
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                ))}

                              {hasSectionAddPermission && (
                                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-purple-400 hover:bg-purple-50 transition-colors">
                                  <button
                                    onClick={() => handleAddContent(section.id)}
                                    className="bg-green-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-green-700 transition-colors flex items-center justify-center gap-2 w-full sm:w-auto">
                                    <Plus className="w-5 h-5" />
                                    <span>Bu Bölüme İçerik Ekle</span>
                                  </button>
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="text-center py-8">
                              <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                              <p className="text-gray-500 mb-4">Bu bölümde henüz içerik yok.</p>
                              {hasSectionAddPermission && (
                                <button
                                  onClick={() => handleAddContent(section.id)}
                                  className="bg-green-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-green-700 rounded-lg flex items-center gap-2 mx-auto">
                                  <Plus className="w-4 h-4" />
                                  İlk İçeriği Ekle
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))
              ) : (
                <div className="bg-white rounded-lg p-8 text-center">
                  <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-6" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Henüz içerik eklenmemiş</h3>
                  <p className="text-gray-500 mb-6">Bu ders için ilk bölümü oluşturarak başlayın.</p>
                  {hasAddPermission && (
                    <button
                      onClick={handleAddSection}
                      className="bg-green-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-green-700 px-6 py-3 rounded-lg flex items-center gap-2 mx-auto">
                      <Plus className="w-5 h-5" />
                      İlk Bölümü Ekle
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        );

      case "stats":
        return (
          <div className="max-w-6xl mx-auto px-6 py-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Toplam Bölüm</p>
                    <p className="text-2xl font-bold text-gray-900">{lesson?.sections?.length || 0}</p>
                  </div>
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                    <BookOpen className="w-6 h-6 text-purple-600" />
                  </div>
                </div>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Toplam İçerik</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {lesson?.sections?.reduce((acc: number, section: any) => acc + (section?.contents?.length || 0), 0) || 0}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <FileText className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg p-8 text-center shadow-sm">
              <BarChart3 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Detaylı İstatistikler</h3>
              <p className="text-gray-500">Gelişmiş analitik raporları yakında eklenecek.</p>
            </div>
          </div>
        );

      case "calendar":
        return hasCalendarPermission ? (
          <AcademicCalendar
            lessonId={lessonId!}
            lesson={lesson}
            onLessonUpdate={async () => {
              const res = await ApiService.getLessonPreview(lessonId!);
              setLesson(res);
            }}
          />
        ) : null;

      default:
        return null;
    }
  };

  const coursesTab: TabItemProps = useMemo(
    () => ({
      type: "custom",
      title: "Dersler",
      path: "lessons",
      actions: (
        <>
          <button
            onClick={() => navigate("/lessons?tab=lessons")}
            className="bg-white px-4 py-2 rounded-md border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 mr-2">
            Tüm Derslere Dön
          </button>
          {hasEditPermission && (
            <a
              href={`/lessons/edit/${lessonId}`}
              className="bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-700 flex items-center gap-2">
              <Edit className="w-4 h-4" />
              Düzenle
            </a>
          )}
        </>
      ),
      isSearch: false,
      showToggle: false,
      customComponent: (
        <div className="h-full overflow-y-auto bg-gray-50">
          <div className="bg-gradient-to-br from-purple-50 to-blue-50 border-b border-gray-200">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
                <div className="lg:col-span-2">
                  <div>
                    <div className="flex items-center justify-between gap-4 mb-4">
                      <h1 className="text-2xl sm:text-4xl font-bold text-gray-900">{lesson?.title}</h1>
                    </div>
                    {lesson?.description && <p className="text-lg text-gray-600 mb-6 leading-relaxed">{lesson.description}</p>}

                    <div className="flex flex-wrap items-center gap-4 sm:gap-6 text-sm text-gray-600 mb-6">
                      <div className="flex items-center">
                        <BookOpen className="w-4 h-4 mr-2 text-purple-600" />
                        <span className="font-medium">{lesson?.sections?.length || 0} Bölüm</span>
                      </div>
                      <span>•</span>
                      <div className="flex items-center">
                        <FileText className="w-4 h-4 mr-2 text-blue-600" />
                        <span className="font-medium">
                          {lesson?.sections?.reduce((acc: number, section: any) => acc + (section?.contents?.length || 0), 0) || 0} İçerik
                        </span>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-3 mb-6">
                      {lesson?.isActive && (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <div className="w-2 h-2 bg-green-400 rounded-full mr-2"></div>
                          Aktif
                        </span>
                      )}
                      {lesson?.isCertificate && (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          <Award className="w-3 h-3 mr-1" />
                          Sertifikalı
                        </span>
                      )}
                      {lesson?.isDownloadable && (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          <Download className="w-3 h-3 mr-1" />
                          İndirilebilir
                        </span>
                      )}
                    </div>

                    {lesson?.content && (
                      <div className="mb-4 p-4 bg-blue-50 rounded-lg border-l-4 border-blue-400">
                        <h3 className="font-semibold text-gray-900 mb-2 flex items-center">
                          <BookOpen className="w-4 h-4 mr-2 text-blue-600" />
                          Ders Hakkında
                        </h3>
                        <p className="text-gray-700 text-sm leading-relaxed">{lesson.content}</p>
                      </div>
                    )}

                    <div className="flex flex-col gap-2 text-sm text-gray-500">
                      <div className="flex items-center">
                        <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                        <span>Oluşturulma: {lesson?.createdAt ? formatDate(lesson.createdAt) : "Belirsiz"}</span>
                      </div>
                      <div className="flex items-center">
                        <Clock className="w-4 h-4 mr-2 text-gray-400" />
                        <span>Son Güncelleme: {lesson?.updatedAt ? formatDate(lesson.updatedAt) : "Belirsiz"}</span>
                      </div>
                    </div>

                    {(hasEditPermission || hasDeletePermission) && (
                      <div className="flex items-center gap-3 mt-2 pt-2 border-t border-gray-100">
                        {hasEditPermission && (
                          <a
                            href={`/lessons/edit/${lessonId}`}
                            className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors flex items-center gap-2"
                            title="Dersi Düzenle">
                            <Edit className="w-4 h-4" />
                            Düzenle
                          </a>
                        )}
                        {hasDeletePermission && (
                          <button
                            onClick={() => navigate(`/lessons/${lessonId}/delete`)}
                            className="bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-red-700 transition-colors flex items-center gap-2"
                            title="Dersi Sil">
                            <Trash2 className="w-4 h-4" />
                            Sil
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="lg:col-span-1">
                  <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
                    <div className="relative mb-6">
                      {lesson?.coverMedia?.url ? (
                        <img src={lesson?.coverMedia?.url} alt={lesson?.title} className="w-full h-48 object-cover rounded-xl" />
                      ) : (
                        <div
                          className={`w-full h-48 bg-gradient-to-br ${getGradientByLessonId(
                            lesson?.id || ""
                          )} rounded-xl flex items-center justify-center`}>
                          <BookOpen className="w-16 h-16 text-white opacity-80" />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-black bg-opacity-20 rounded-xl flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity cursor-pointer">
                        <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center backdrop-blur-sm">
                          <Play className="w-8 h-8 text-white ml-1" />
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <button
                        className="w-full bg-purple-600 text-white px-6 py-4 rounded-xl font-semibold text-lg hover:bg-purple-700 shadow-lg"
                        onClick={() => {
                          if (lessonProgress?.contentId) {
                            navigate(`/lessons/play/${lessonId}?contentId=${lessonProgress.contentId}`);
                          } else {
                            navigate(`/lessons/play/${lessonId}`);
                          }
                        }}>
                        {lessonProgress ? "Kaldığım Yerden Devam Et" : "Öğrenmeye Başla"}
                      </button>
                      {lessonProgress && (
                        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-3 text-center">
                          <div className="text-xs text-blue-600 font-medium mb-1">Son Kaldığınız Yer</div>
                          <div className="flex items-center justify-center gap-2 mb-1">
                            <Layers3 className="w-4 h-4 text-blue-600" />
                            <span className="font-medium text-gray-800">{lessonProgress.sectionTitle}</span>
                          </div>
                          <div className="flex items-center justify-center gap-2">
                            {getProgressContentIcon(lessonProgress.contentType)}
                            <span className="text-gray-700">{lessonProgress.contentTitle}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {renderCourseContentTabs()}

          <div className="bg-gray-50">{renderActiveContentTab()}</div>
        </div>
      ),
    }),
    [lesson, lessonId, navigate, activeContentTab, expandedSections, toggleSection]
  );

  const homeworksTab: TabItemProps = useMemo(() => {
    const canListHomeworks = checkPermission("homework_list");
    return {
      type: "custom",
      title: "Ödevler",
      path: "homeworks",
      actions: null,
      isSearch: false,
      showToggle: false,
      customComponent: (
        <div className="h-full flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Ödevler sayfasına yönlendiriliyorsunuz...</p>
          </div>
        </div>
      ),
      __visible: canListHomeworks as any,
    };
  }, []);

  const deficienciesTab: TabItemProps = useMemo(
    () => ({
      type: "custom",
      title: "Eksikler",
      path: "deficiencies",
      actions: null,
      isSearch: false,
      showToggle: false,
      customComponent: (
        <div className="h-full flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Eksikler sayfasına yönlendiriliyorsunuz...</p>
          </div>
        </div>
      ),
    }),
    []
  );

  const reportsTab: TabItemProps = useMemo(
    () => ({
      type: "custom",
      title: "Raporlar",
      path: "reports",
      actions: null,
      isSearch: false,
      showToggle: false,
      customComponent: (
        <div className="h-full flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Raporlar sayfasına yönlendiriliyorsunuz...</p>
          </div>
        </div>
      ),
    }),
    []
  );

  const errorBookletTab: TabItemProps = useMemo(
    () => ({
      type: "custom",
      title: "Hata Kitapçığı",
      path: "error-booklet",
      actions: null,
      isSearch: false,
      showToggle: false,
      customComponent: (
        <div className="h-full flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Hata kitapçığı sayfasına yönlendiriliyorsunuz...</p>
          </div>
        </div>
      ),
      __visible: !isParentOnly,
    }),
    [isParentOnly]
  );

  const tabs = useMemo(() => {
    return [coursesTab, homeworksTab, deficienciesTab, errorBookletTab, reportsTab].filter((t: any) => t.__visible !== false);
  }, [coursesTab, homeworksTab, deficienciesTab, errorBookletTab, reportsTab]);

  if (!lesson) {
    return (
      <div className="h-[88vh] bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Ders bilgileri yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <TabPage title="E-Eğitim" tabs={tabs} />
      <SectionModal lessonId={lessonId || ""} isOpen={isSectionModalOpen} onClose={handleCloseSectionModal} />
      <ContentModal lessonId={lessonId || ""} sectionId={selectedSectionId} isOpen={isContentModalOpen} onClose={handleContentModalClose} />
      {editModalState.isOpen && (
        <EditFormModal editModalState={editModalState} setEditModalState={setEditModalState} handleSaveEdit={handleSaveEdit} />
      )}

      <Modal
        isOpen={isVideoGainsModalOpen}
        onClose={handleCloseVideoGainsModal}
        title={selectedVideoContent ? `Video Kazanımları - ${selectedVideoContent.title}` : "Video Kazanımları"}
        size="lg">
        <div className="space-y-4 sm:space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4">
            <div className="flex items-center gap-2 text-blue-800">
              <Award className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="font-medium text-sm sm:text-base">Bilgi</span>
            </div>
            <p className="text-blue-700 text-xs sm:text-sm mt-1">
              Video içeriğine özel kazanımlar ekleyebilirsiniz. Her kazanım için başlangıç ve bitiş zamanlarını belirtmeniz gerekmektedir.
            </p>
          </div>

          {isLoadingGains ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
            </div>
          ) : (
            <>
              <div className="space-y-3">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Award className="w-4 h-4 sm:w-5 sm:h-5" />
                  Mevcut Kazanımlar ({videoGains.length})
                </h3>

                {videoGains.length === 0 ? (
                  <div className="text-center py-6 sm:py-8 text-gray-500">
                    <Award className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-3 text-gray-300" />
                    <p className="text-sm sm:text-base">Henüz kazanım eklenmemiş.</p>
                  </div>
                ) : (
                  <div className="max-h-40 sm:max-h-32 overflow-y-auto space-y-2 pr-1 sm:pr-2">
                    {videoGains.map((gain) => (
                      <div
                        key={gain.id}
                        className="border border-gray-200 rounded-lg p-3 sm:p-4 bg-gray-50 hover:bg-gray-100 transition-colors">
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 mb-1 text-sm sm:text-base break-words">{gain.gain}</p>
                            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-xs sm:text-sm text-gray-600">
                              <div className="flex items-center gap-1">
                                <Clock className="w-3 h-3 sm:w-4 sm:h-4" />
                                <span>
                                  {formatDisplayTime(gain.startTime)} - {formatDisplayTime(gain.endTime)}
                                </span>
                              </div>
                              {gain.createdAt && (
                                <span className="hidden sm:inline">{new Date(gain.createdAt).toLocaleDateString("tr-TR")}</span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-1 sm:gap-2 self-end sm:self-start flex-shrink-0">
                            <button
                              onClick={() => handleEditGain(gain)}
                              className="p-1.5 sm:p-1 text-blue-600 hover:bg-blue-100 rounded text-xs sm:text-sm"
                              title="Düzenle">
                              <Edit className="w-3 h-3 sm:w-4 sm:h-4" />
                            </button>
                            <button
                              onClick={() => handleOpenDeleteConfirm(gain.id, gain.gain)}
                              className="p-1.5 sm:p-1 text-red-600 hover:bg-red-100 rounded text-xs sm:text-sm"
                              title="Sil">
                              <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="border-t pt-4 sm:pt-6">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">
                  {editingGain ? "Kazanımı Düzenle" : "Yeni Kazanım Ekle"}
                </h3>

                <div className="space-y-3 sm:space-y-4">
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Kazanım Açıklaması</label>
                    <textarea
                      value={newGain.gain}
                      onChange={(e) => setNewGain({ ...newGain, gain: e.target.value })}
                      placeholder="Kazanım açıklamasını girin..."
                      className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500 resize-none"
                      rows={3}
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                        Başlangıç Zamanı (Dakika:Saniye)
                      </label>
                      <input
                        type="text"
                        placeholder="00:00"
                        pattern="([0-5]?[0-9]):[0-5][0-9]"
                        value={newGain.startTime}
                        onChange={(e) => handleTimeInputChange("startTime", e.target.value)}
                        className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500"
                      />
                    </div>

                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                        Bitiş Zamanı (Dakika:Saniye)
                      </label>
                      <input
                        type="text"
                        placeholder="00:00"
                        pattern="([0-5]?[0-9]):[0-5][0-9]"
                        value={newGain.endTime}
                        onChange={(e) => handleTimeInputChange("endTime", e.target.value)}
                        className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500"
                      />
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 pt-2">
                    <button
                      onClick={editingGain ? handleUpdateGain : handleCreateGain}
                      disabled={!newGain.gain || !newGain.startTime || !newGain.endTime || isUpdating}
                      className="flex items-center justify-center gap-2 px-4 py-2.5 sm:py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base font-medium">
                      {isUpdating ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      ) : (
                        <Save className="w-4 h-4" />
                      )}
                      {editingGain ? (isUpdating ? "Güncelleniyor..." : "Güncelle") : "Kaydet"}
                    </button>

                    {editingGain && (
                      <button
                        onClick={() => {
                          setEditingGain(null);
                          setNewGain({ gain: "", startTime: "", endTime: "" });
                        }}
                        className="px-4 py-2.5 sm:py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm sm:text-base font-medium">
                        İptal
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </Modal>

      <ConfirmModal
        isOpen={deleteConfirmModal.isOpen}
        onClose={handleCloseDeleteConfirm}
        onConfirm={handleConfirmDelete}
        title="Kazanımı Sil"
        message={`"${deleteConfirmModal.gainTitle}" kazanımını silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.`}
        confirmText={isDeleting ? "Siliniyor..." : "Sil"}
        cancelText="İptal"
        confirmButtonVariant="danger"
        isLoading={isDeleting}
      />

      {selectedTestContent && (
        <TestQuestionsModal
          isOpen={isTestQuestionsModalOpen}
          onClose={handleCloseTestQuestionsModal}
          lessonId={lessonId!}
          sectionId={selectedTestContent.sectionId}
          contentId={selectedTestContent.id}
          contentTitle={selectedTestContent.title}
        />
      )}

      <Modal
        isOpen={learningPlanModal.isOpen}
        onClose={handleCloseLearningPlanModal}
        title={`Öğrenme Planım — ${learningPlanModal.title}`}
        size="xl"
      >
        {learningPlanModal.loading ? (
          <div className="py-8 text-center text-gray-500">Plan hazırlanıyor...</div>
        ) : learningPlanModal.data?.error ? (
          <div className="py-8 text-center text-red-600">{learningPlanModal.data.error}</div>
        ) : learningPlanModal.data ? (
          <div className="flex flex-col gap-3 max-h-[75vh]">
            <div className="flex flex-wrap items-center gap-2 text-xs border-b border-gray-100 pb-3">
              <span className="px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 font-semibold">
                {learningPlanModal.data.testCount || 0} test çözdüm
              </span>
              <span className="px-2 py-0.5 rounded-full bg-orange-50 text-orange-700 font-semibold">
                {learningPlanModal.data.wrongCount || 0} yanlış soru
              </span>
              <span className="px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 font-semibold">
                {learningPlanModal.data.conceptCount || 0} odak konsept
              </span>
              <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">
                {(learningPlanModal.data.materials?.videos || []).length} video,{" "}
                {(learningPlanModal.data.materials?.pdfs || []).length} PDF
              </span>
              <button
                type="button"
                onClick={() => void handleOpenLearningPlanModal({ id: learningPlanModal.sectionId, title: learningPlanModal.title }, true)}
                className="ml-auto inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-semibold text-gray-600 border border-gray-200 hover:bg-gray-50"
                title="Cache'i yenile, baştan hesapla"
              >
                <RefreshCw className="w-3 h-3" />
                Yenile
              </button>
            </div>

            {learningPlanModal.data.message && (
              <div className="py-4 px-3 rounded-lg bg-yellow-50 border border-yellow-100 text-sm text-yellow-800">
                {learningPlanModal.data.message}
              </div>
            )}

            {(learningPlanModal.data.conceptGroups || []).length === 0 ? (
              <div className="py-6 text-center text-gray-500 text-sm">
                Bu bölümde henüz plan üretecek yanlış cevap kanıtı yok.<br />
                Birkaç test çözünce burada öğrenme önceliklerini göreceksin.
              </div>
            ) : (
              <>
              <div className="flex-1 overflow-y-auto pr-1 space-y-2">
                {(() => {
                  const videoTitleMap: Record<string, string> = (learningPlanModal.data.materials?.videos || [])
                    .reduce((acc: Record<string, string>, v: any) => {
                      acc[v.id] = v.title;
                      return acc;
                    }, {});

                  return learningPlanModal.data.conceptGroups.map((c: any, idx: number) => (
                  <div key={c.conceptKey || idx} className="border border-gray-200 rounded-xl p-3 bg-white">
                    <div className="flex items-start gap-2">
                      <div className="shrink-0 mt-0.5 px-2 py-0.5 rounded-full bg-blue-600 text-white text-xs font-bold">
                        #{idx + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <Target className="w-4 h-4 text-blue-600 shrink-0" />
                          <span className="font-semibold text-gray-900 text-sm leading-5 break-words">
                            {c.conceptLabel}
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-1.5 mt-1.5 text-xs">
                          <span className="px-1.5 py-0.5 rounded bg-orange-100 text-orange-700">
                            {c.wrongCount} yanlış kanıt
                          </span>
                          <span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-700">
                            {c.distinctExamCount} farklı test
                          </span>
                          <span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-700">
                            {c.distinctQuestionCount} farklı soru
                          </span>
                          <span className="px-1.5 py-0.5 rounded bg-purple-50 text-purple-700">
                            skor {Number(c.score || 0).toFixed(1)}
                          </span>
                        </div>
                        {(c.videoSegments || []).length > 0 && (
                          <div className="mt-2 pt-2 border-t border-dashed border-gray-100">
                            <div className="text-[11px] font-semibold text-gray-500 mb-1">
                              Eşleşen video pasajları
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                              {(c.videoSegments || []).map((v: any, vi: number) => {
                                const title = videoTitleMap[v.videoId] || v.title || 'Video';
                                return (
                                  <span
                                    key={vi}
                                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-50 border border-green-200 text-green-800 text-[11px]"
                                  >
                                    <Video className="w-3 h-3" />
                                    <span className="font-medium">{title}</span>
                                    <span className="font-mono text-green-700/80">
                                      {v.startTime} – {v.endTime}
                                    </span>
                                  </span>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ));
                })()}
              </div>

              <div className="border-t border-gray-100 pt-3 mt-2 flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-between">
                <p className="text-xs text-gray-500">
                  AI öğretmen bu odak konseptleri sırayla anlatacak ve gerekli video segmentlerini açacak.
                </p>
                <button
                  type="button"
                  onClick={() => void handleStartLessonFromPlan()}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 transition-colors"
                >
                  <Brain className="w-4 h-4" />
                  Bu Planla Derse Başla
                </button>
              </div>
              </>
            )}
          </div>
        ) : null}
      </Modal>

      <Modal isOpen={transcriptModal.isOpen} onClose={handleCloseTranscriptModal} title={transcriptModal.title} size="lg">
        {transcriptModal.loading ? (
          <div className="py-8 text-center text-gray-500">Transkript yükleniyor...</div>
        ) : transcriptModal.data?.error ? (
          <div className="py-8 text-center text-red-600">{transcriptModal.data.error}</div>
        ) : transcriptModal.data ? (
          <div className="flex flex-col gap-3 max-h-[70vh]">
            <div className="flex flex-wrap items-center gap-2 text-xs text-gray-600 border-b border-gray-100 pb-3">
              <span className="px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 font-semibold">
                {transcriptModal.data.segmentCount || 0} segment
              </span>
              {transcriptModal.data.transcription?.source && (
                <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">
                  Kaynak: {transcriptModal.data.transcription.source}
                </span>
              )}
              {transcriptModal.data.transcription?.vimeoId && (
                <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">
                  Vimeo ID: {transcriptModal.data.transcription.vimeoId}
                </span>
              )}
              {transcriptModal.data.transcription?.status && (
                <span className="px-2 py-0.5 rounded-full bg-green-50 text-green-700">
                  {transcriptModal.data.transcription.status}
                </span>
              )}
            </div>

            {transcriptModal.data.segmentCount > 0 ? (
              <>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={transcriptModal.searchTerm}
                    onChange={(e) =>
                      setTranscriptModal((prev) => ({ ...prev, searchTerm: e.target.value }))
                    }
                    placeholder="Transkript içinde ara..."
                    className="w-full pl-10 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="flex-1 overflow-y-auto pr-1 space-y-1.5">
                  {(transcriptModal.data.segments as any[])
                    .filter((seg: any) => {
                      const term = transcriptModal.searchTerm.trim().toLocaleLowerCase("tr-TR");
                      if (!term) return true;
                      return String(seg.content || "").toLocaleLowerCase("tr-TR").includes(term);
                    })
                    .map((seg: any) => (
                      <div
                        key={seg.id}
                        className="flex gap-3 py-1.5 px-2 rounded-md text-sm hover:bg-gray-50">
                        <span className="shrink-0 text-xs font-mono text-gray-500 mt-0.5">
                          {seg.startTime} → {seg.endTime}
                        </span>
                        <span className="text-gray-800 leading-relaxed break-words">{seg.content}</span>
                      </div>
                    ))}
                </div>
              </>
            ) : (
              <div className="py-6 text-center text-gray-500 text-sm">
                Bu video için henüz transkript bulunmuyor.
              </div>
            )}
          </div>
        ) : null}
      </Modal>
    </>
  );
}
